import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  browserPopupRedirectResolver,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  initializeAuth,
  inMemoryPersistence,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type AuthProvider,
  type User,
} from "firebase/auth";
import type { IdentityProvider, IdentityUser } from "./identity.js";

/**
 * Firebase Auth (web 12, Auth only) behind the IdentityProvider port.
 *
 * - `inMemoryPersistence`: Firebase's own tokens are never written to
 *   localStorage/sessionStorage/IndexedDB. Firebase is only used to get an ID
 *   token for POST /users/login; the app session is the backend cookie.
 * - A closed/cancelled popup resolves null (silent). A blocked popup falls
 *   back to a full-page redirect; `completeRedirect()` picks it up on return.
 * - `emulatorHost` (E2E only, set by the Playwright webServer config) points
 *   Auth at the Firebase Auth emulator. Deploys never set it.
 */

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  emulatorHost?: string;
}

const CANCELLED = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

function toIdentityUser(user: User): IdentityUser {
  return {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName,
    providerId: user.providerData[0]?.providerId,
    getIdToken: () => user.getIdToken(),
  };
}

export function createFirebaseIdentity(config: FirebaseWebConfig): IdentityProvider & { auth: Auth } {
  const app: FirebaseApp = initializeApp({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    appId: config.appId,
  });
  const auth = initializeAuth(app, {
    persistence: inMemoryPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  });
  if (config.emulatorHost) {
    connectAuthEmulator(auth, `http://${config.emulatorHost}`, { disableWarnings: true });
  }

  const popup = async (provider: AuthProvider): Promise<IdentityUser | null> => {
    try {
      const cred = await signInWithPopup(auth, provider);
      return toIdentityUser(cred.user);
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      if (CANCELLED.has(code)) return null;
      if (code === "auth/popup-blocked") {
        await signInWithRedirect(auth, provider);
        // The page navigates away; this promise never needs to settle.
        return new Promise<IdentityUser | null>(() => undefined);
      }
      throw err;
    }
  };

  const apple = () => {
    const p = new OAuthProvider("apple.com");
    p.addScope("email");
    p.addScope("name");
    return p;
  };

  return {
    auth,
    async signInWithPassword(email, password) {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      return toIdentityUser(cred.user);
    },
    async createUserWithPassword(email, password) {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      return toIdentityUser(cred.user);
    },
    async getIdToken() {
      return auth.currentUser ? auth.currentUser.getIdToken() : null;
    },
    signInWithGoogle: () => popup(new GoogleAuthProvider()),
    // Apple is offered on every browser (parity decision), not only Safari.
    signInWithApple: () => popup(apple()),
    async completeRedirect() {
      const result = await getRedirectResult(auth);
      return result ? toIdentityUser(result.user) : null;
    },
    signOut: () => signOut(auth),
  };
}
